import { Pipe, PipeTransform } from '@angular/core';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { LanguageService } from '../../core/services/language.service';
import { UserConfigurationService } from '../../core/services/user-configuration.service';
import { DueDateInfo } from '../contract/due-date-info.contract';
import { DimensionSystem } from '../enums/dimension-system.enum';
import { RemainingValueType } from '../enums/remaining-value-type.enum';
import { WarningLevel } from '../enums/warning-level.enum';
import { FormatService } from '../services/format.service';
import { DimensionSystemService } from '../services/dimension-system.service';

@Pipe({
  name: 'dueDateRemainingValue',
  standalone: true,
})
export class DueDateRemainingValuePipe implements PipeTransform {
  constructor(
    private languageService: LanguageService,
    private userConfigurationService: UserConfigurationService,
    private formatService: FormatService,
    private dimensionSystemService: DimensionSystemService
  ) { }

  public transform(dueDateInfo: DueDateInfo): string {
    if (!dueDateInfo || isEmpty(dueDateInfo)) {
      return '';
    }

    const isOverdue = dueDateInfo.warningLevel === WarningLevel.OVERDUE || dueDateInfo.remainingValue < 0;
    const isSingular = Math.abs(dueDateInfo.remainingValue) === 1;

    if (dueDateInfo.remainingValueType === RemainingValueType.DAYS) {
      const dueDate = moment(dueDateInfo.dueDate).startOf('day');
      const currDate = moment().startOf('day');
      const absoluteRemainingValue = this.formatService.formatNumber(Math.abs(currDate.diff(dueDate, 'days')));

      return this.languageService.getInstant(this.getRemainingStringDays(isOverdue, isSingular), {
        value: absoluteRemainingValue,
      });
    }


    if (dueDateInfo.remainingValueType === RemainingValueType.MILEAGE) {
      const remainingValueInDimensionUnit =
        this.dimensionSystemService.toUserMeasure(dueDateInfo.remainingValue,'km');
      const remainingValueForDueTypeMileage =
        this.formatService.formatNumber(Math.abs(remainingValueInDimensionUnit));
      return this.languageService.getInstant(this.getRemainingStringMileage(isOverdue, isSingular), {
        value: remainingValueForDueTypeMileage,
      });
    }

    const remainingValue = this.formatService.formatNumber(Math.abs(dueDateInfo.remainingValue));
    return this.languageService.getInstant(this.getRemainingStringOperatingHours(isOverdue, isSingular), {
      value: remainingValue,
    });
  }

  private getRemainingStringDays(isOverdue: boolean, isSingular: boolean): string {
    if (isOverdue) {
      return isSingular
        ? 'shared.pipes.dueDateRemainingValue.overdue.day.single'
        : 'shared.pipes.dueDateRemainingValue.overdue.day.multiple';
    } else {
      return isSingular
        ? 'shared.pipes.dueDateRemainingValue.notOverdue.day.single'
        : 'shared.pipes.dueDateRemainingValue.notOverdue.day.multiple';
    }
  }

  private getRemainingStringMileage(isOverdue: boolean, isSingular: boolean): string {
    if (isOverdue) {
      if (this.isMetricSystem()) {
        return isSingular
          ? 'shared.pipes.dueDateRemainingValue.overdue.km.single'
          : 'shared.pipes.dueDateRemainingValue.overdue.km.multiple';
      } else {
        return isSingular
          ? 'shared.pipes.dueDateRemainingValue.overdue.mi.single'
          : 'shared.pipes.dueDateRemainingValue.overdue.mi.multiple';
      }
    } else {
      if (this.isMetricSystem()) {
        return isSingular
          ? 'shared.pipes.dueDateRemainingValue.notOverdue.km.single'
          : 'shared.pipes.dueDateRemainingValue.notOverdue.km.multiple';
      } else {
        return isSingular
          ? 'shared.pipes.dueDateRemainingValue.notOverdue.mi.single'
          : 'shared.pipes.dueDateRemainingValue.notOverdue.mi.multiple';
      }
    }
  }

  private getRemainingStringOperatingHours(isOverdue: boolean, isSingular: boolean): string {
    if (isOverdue) {
      return isSingular
        ? 'shared.pipes.dueDateRemainingValue.overdue.h.single'
        : 'shared.pipes.dueDateRemainingValue.overdue.h.multiple';
    } else {
      return isSingular
        ? 'shared.pipes.dueDateRemainingValue.notOverdue.h.single'
        : 'shared.pipes.dueDateRemainingValue.notOverdue.h.multiple';
    }
  }

  private isMetricSystem(): boolean {
    return this.userConfigurationService.getUserDimensionSystem() === DimensionSystem.METRIC;
  }
}
